/** @jsxImportSource theme-ui */
import { Box, Card, Flex, Heading, Text } from 'theme-ui';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useGetCompanyDeletionSchedulesQuery } from '@/store/apiService';
import { COMPANY_DELETION_SCHEDULE_STATUS } from '@/types/types';
import { CancelDeletionScheduleButton } from '@/containers/companyDeletionSchedules/CancelDeletionScheduleButton';
import { licenseActionLabel } from '@/containers/companyDeletionSchedules/labels';

const CompanyDeletionSchedule = () => {
    const { id } = useParams<{ id: string }>();
    const schedules = useGetCompanyDeletionSchedulesQuery(
        { CompanyId: id, Status: COMPANY_DELETION_SCHEDULE_STATUS.Scheduled },
        { skip: !id }
    );
    const schedule = schedules.data?.Results?.[0];

    if (!id || !schedule) {
        return null;
    }

    return (
        <Card
            variant="widget"
            sx={{ borderLeft: '4px solid', borderLeftColor: 'danger' }}
            data-testid="company-deletion-schedule"
        >
            <Flex sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}>
                <Box>
                    <Heading as="h3" color="danger" mb={2}>
                        Scheduled for deletion
                        {schedule.ScheduledDeletionDate
                            ? ` on ${format(
                                  new Date(schedule.ScheduledDeletionDate),
                                  'd MMM yyyy'
                              )}`
                            : ''}
                    </Heading>
                    <Text as="p">
                        Marked by {schedule.CreatedBy ?? 'unknown'} on{' '}
                        {format(new Date(schedule.CreatedDate), 'd MMM yyyy, HH:mm')}
                    </Text>
                    {schedule.Reason ? (
                        <Text as="p">
                            <strong>Reason:</strong> {schedule.Reason}
                        </Text>
                    ) : null}
                    <Text as="p">
                        <strong>Licenses:</strong>{' '}
                        {licenseActionLabel(schedule.MetaData?.LicenseTerminationTiming)}
                        {schedule.MetaData?.LicensesValidTo
                            ? ` (valid to ${format(
                                  new Date(schedule.MetaData.LicensesValidTo),
                                  'd MMM yyyy'
                              )})`
                            : ''}
                    </Text>
                    {schedule.MetaData?.InactivityNotificationDate ? (
                        <Text as="p">
                            <strong>Inactivity warning sent:</strong>{' '}
                            {format(
                                new Date(schedule.MetaData.InactivityNotificationDate),
                                'd MMM yyyy'
                            )}{' '}
                            (this mark replaced the automatic inactivity deletion)
                        </Text>
                    ) : null}
                    {schedule.VossCustomerId || schedule.VossCustomerNote ? (
                        <Text as="p">
                            <strong>Voss customer:</strong> {schedule.VossCustomerId ?? ''}{' '}
                            {schedule.VossCustomerNote ?? ''}
                        </Text>
                    ) : null}
                    {(schedule.Warnings ?? []).map((warning) => (
                        <Text as="p" key={warning} color="danger">
                            {warning}
                        </Text>
                    ))}
                </Box>
                <CancelDeletionScheduleButton companyId={id} />
            </Flex>
        </Card>
    );
};

export default CompanyDeletionSchedule;
